import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
// Fuente única de verdad (también la escriben backend/scripts/*_translations.py);
// se empaqueta en el bundle para que el español nunca dependa de un fetch en runtime.
import esTranslation from '../public/locales/es/translation.json';

// IDIOMAS SOPORTADOS - Solo español, lenguas cooficiales y europeos principales
export const ALL_LANGUAGES = [
  // Idiomas oficiales de España (prioritarios)
  { code: 'es', name: 'Spanish', flag: '🇪🇸', native: 'Español', region: 'spain' },
  { code: 'ca', name: 'Catalan', flag: '🏴󠁥󠁳󠁣󠁴󠁿', native: 'Català', region: 'spain' },
  { code: 'gl', name: 'Galician', flag: '🏴󠁥󠁳󠁧󠁡󠁿', native: 'Galego', region: 'spain' },
  { code: 'eu', name: 'Basque', flag: '🏴󠁥󠁳󠁰󠁶󠁿', native: 'Euskara', region: 'spain' },
  
  // Idiomas europeos principales
  { code: 'en', name: 'English', flag: '🇬🇧', native: 'English', region: 'europe' },
  { code: 'fr', name: 'French', flag: '🇫🇷', native: 'Français', region: 'europe' },
  { code: 'de', name: 'German', flag: '🇩🇪', native: 'Deutsch', region: 'europe' },
  { code: 'it', name: 'Italian', flag: '🇮🇹', native: 'Italiano', region: 'europe' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹', native: 'Português', region: 'europe' },
];

// Todos los idiomas están completamente traducidos (usando traducción automática de Google)
// Se eliminó el estado "Beta" - todos los idiomas funcionan al 100%
export const FULLY_TRANSLATED = ALL_LANGUAGES.map(l => l.code);

// Idiomas que se muestran en el selector (todos)
export const SUPPORTED_LANGUAGES = ALL_LANGUAGES;

// Codigos de idiomas soportados
export const SUPPORTED_LANGUAGE_CODES = ALL_LANGUAGES.map(l => l.code);

// Agrupar idiomas por region (solo regiones activas: spain, europe)
export const LANGUAGES_BY_REGION = {
  spain: ALL_LANGUAGES.filter(l => l.region === 'spain'),
  europe: ALL_LANGUAGES.filter(l => l.region === 'europe'),
};

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Producto mono-oposición (Baleares): idioma fijo español.
    // Se fuerza 'es' por encima del detector; los ficheros de traducción se
    // conservan por si se reactivan más adelante.
    lng: 'es',
    fallbackLng: 'es',
    supportedLngs: SUPPORTED_LANGUAGE_CODES,
    debug: process.env.NODE_ENV === 'development',
    defaultNS: 'translation',

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
      htmlTag: document.documentElement
    },

    // Español empaquetado: si falla la carga HTTP de /locales/* (CDN, service
    // worker, rewrites) la UI nunca se queda mostrando claves crudas.
    resources: {
      es: { translation: esTranslation },
    },
    partialBundledLanguages: true,

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    
    interpolation: {
      escapeValue: false,
      format: (value, format) => {
        if (format === 'uppercase') return value.toUpperCase();
        if (format === 'lowercase') return value.toLowerCase();
        return value;
      }
    },
    
    react: {
      useSuspense: true
    }
  });

// Helper functions
export const changeLanguage = (lng) => {
  i18n.changeLanguage(lng);
  document.documentElement.lang = lng;
  
  // Set RTL for Arabic, Hebrew, Persian, Urdu
  const rtlLangs = ['ar', 'he', 'fa', 'ur'];
  document.documentElement.dir = rtlLangs.includes(lng) ? 'rtl' : 'ltr';
  
  localStorage.setItem('i18nextLng', lng);
  window.dispatchEvent(new CustomEvent('languageChanged', { detail: lng }));
};

export const getCurrentLanguage = () => i18n.language || 'es';

export const isLanguageSupported = (lng) => SUPPORTED_LANGUAGE_CODES.includes(lng);

export const getLanguageInfo = (code) => ALL_LANGUAGES.find(l => l.code === code) || null;

export const isFullyTranslated = (code) => FULLY_TRANSLATED.includes(code);

export const isRTL = (code) => {
  const lang = getLanguageInfo(code);
  return lang?.rtl || false;
};

export default i18n;
